import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Aurora } from '@/components/Aurora';
import { BrandMark } from '@/components/BrandMark';
import { GlassButton } from '@/components/GlassButton';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface LocationState { from?: string }

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, requestCode, verify } = useAuth();
  const redirectTo = (location.state as LocationState | null)?.from ?? '/app';

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);

  useEffect(() => {
    if (user !== null) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo]);

  async function onRequest(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const r = await requestCode(email);
      if (r.devCode !== undefined) {
        setDevCode(r.devCode);
        // Only auto-fill on localhost / preview deploys — protects against
        // accidentally leaking the code on a real shared demo if EXPOSE_MAGIC_CODE
        // ever stays on by mistake.
        const isLocalish = /(^localhost$|^127\.|\.pages\.dev$)/.test(window.location.hostname);
        if (isLocalish) setCode(r.devCode);
      }
      setStage('code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '请求失败');
    } finally {
      setPending(false);
    }
  }

  async function onVerify(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await verify(email, code);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '验证失败');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="relative min-h-[calc(100vh-4rem)] grid place-items-center px-5 py-12">
      <div className="absolute inset-0 -z-10 opacity-50">
        <Aurora colorStops={['#00f0ff', '#aaff00', '#ff2e88']} amplitude={0.9} blend={0.6} />
      </div>
      <div className="absolute inset-0 -z-10 bg-ink-950/40" />

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <BrandMark size="lg" />
          <h1 className="mt-6 font-display text-3xl font-extrabold text-white">
            {stage === 'email' ? '登录 / 注册' : '查收验证码'}
          </h1>
          <p className="mt-2 text-sm text-cream-400">
            {stage === 'email'
              ? '无密码 · 邮箱 6 位验证码 · 30 秒搞定'
              : `验证码已发送到 ${email}`}
          </p>
        </div>

        <form
          onSubmit={stage === 'email' ? onRequest : onVerify}
          className="glass-card rounded-3xl p-6 space-y-4"
        >
          {stage === 'email' ? (
            <label className="block">
              <span className="text-xs text-cream-400 uppercase tracking-wider font-semibold">邮箱</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                autoFocus
                autoComplete="email"
                disabled={pending}
                className="mt-2 w-full rounded-2xl bg-white/[0.04] border border-white/[0.08] px-4 py-3 text-white placeholder:text-cream-400/50 focus:outline-none focus:border-honey-400/60 focus:bg-white/[0.06] transition-all disabled:opacity-50"
              />
            </label>
          ) : (
            <>
              <label className="block">
                <span className="text-xs text-cream-400 uppercase tracking-wider font-semibold">6 位验证码</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  disabled={pending}
                  className="mt-2 w-full rounded-2xl bg-white/[0.04] border border-white/[0.08] px-4 py-3 text-white text-center font-mono text-2xl tracking-[0.5em] placeholder:text-cream-400/30 focus:outline-none focus:border-honey-400/60 focus:bg-white/[0.06] transition-all disabled:opacity-50"
                />
              </label>
              {devCode !== null && (
                <div className="rounded-xl bg-honey-500/[0.08] border border-honey-400/30 px-3 py-2 text-xs">
                  <span className="text-honey-300 font-arcade tracking-widest mr-2">DEV</span>
                  <span className="text-cream-300">演示环境直接返回验证码：</span>
                  <span className="font-mono text-honey-200 ml-1">{devCode}</span>
                </div>
              )}
            </>
          )}

          {error !== null && (
            <div className="text-xs text-ember-300 rounded-xl bg-ember-500/[0.08] border border-ember-500/30 px-3 py-2">
              ⚠ {error}
            </div>
          )}

          <GlassButton type="submit" size="lg" variant="solid" disabled={pending} className="w-full">
            {pending ? '处理中…' : stage === 'email' ? '发送验证码 →' : '进入学习 →'}
          </GlassButton>

          {stage === 'code' && (
            <button
              type="button"
              onClick={() => { setStage('email'); setError(null); setDevCode(null); setCode(''); }}
              className="block w-full text-center text-sm text-cream-400 hover:text-white transition-colors"
            >
              ← 换个邮箱
            </button>
          )}
        </form>

        <p className="mt-6 text-center text-xs text-cream-400">
          继续即表示同意 <a href="#" className="underline">用户协议</a> 与 <a href="#" className="underline">隐私政策</a>
        </p>
      </div>
    </div>
  );
}
